"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/fabrication/laser-cutting/applications";

const LEFT_ITEMS = [
	{ titleKey: "left1Title", descKey: "left1Desc" },
	{ titleKey: "left2Title", descKey: "left2Desc" },
	{ titleKey: "left3Title", descKey: "left3Desc" },
	{ titleKey: "left4Title", descKey: "left4Desc" },
	{ titleKey: "left5Title", descKey: "left5Desc" },
	{ titleKey: "left6Title", descKey: "left6Desc" },
] as const;

type RightCard = {
	image: string;
	labelKey: "card1Label" | "card2Label" | "card3Label" | "card4Label" | "card5Label" | "card6Label";
	href: string;
};

const RIGHT_CARDS: readonly RightCard[] = [
	{ image: `${IMAGE_BASE}/aerospace.webp`, labelKey: "card1Label", href: "/industry/aerospace" },
	{ image: `${IMAGE_BASE}/automotive.webp`, labelKey: "card2Label", href: "/industry/automotive" },
	{ image: `${IMAGE_BASE}/electronics.webp`, labelKey: "card3Label", href: "/industry/consumer-electronics" },
	{ image: `${IMAGE_BASE}/medical-devices.webp`, labelKey: "card4Label", href: "/industry/medical-device" },
	{ image: `${IMAGE_BASE}/industrial-machinery.webp`, labelKey: "card5Label", href: "/industry/industry-equipment" },
	{ image: `${IMAGE_BASE}/new-energy-equipment.webp`, labelKey: "card6Label", href: "/industry/renewable-energy" },
];

export default function LaserCuttingApplications() {
	const t = useTranslations("laserCuttingApplicationsSection");

	return (
		<section className="py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<p className="mb-3 text-sm font-medium uppercase tracking-wide text-[#78b63f]">{t("eyebrow")}</p>
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>
				</div>

				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1.3fr_0.9fr] lg:gap-8 xl:gap-10">
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-x-10 sm:gap-y-8">
						{LEFT_ITEMS.map((item) => (
							<article key={item.titleKey}>
								<h3 className="mb-2 text-lg font-bold leading-tight text-[#78b63f] sm:text-xl md:text-2xl">
									{t(item.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
							</article>
						))}
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						{RIGHT_CARDS.map((card) => (
							<Link
								key={card.labelKey}
								href={card.href}
								className="group rounded-2xl bg-[#ececec] p-4 text-black transition-colors duration-300 hover:bg-[#84cf3f] hover:text-white focus-visible:bg-[#84cf3f] focus-visible:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#78b63f]"
							>
								<div className="relative mb-6 h-[64px] w-[64px] overflow-hidden rounded-full bg-white/70">
									<Image
										src={encodeURI(card.image)}
										alt={t(card.labelKey)}
										fill
										className="object-cover"
										sizes="64px"
									/>
								</div>
								<p className="text-right text-lg font-semibold transition-colors duration-300 sm:text-xl">{t(card.labelKey)}</p>
							</Link>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
